import { Tooltip } from 'antd';
import React, { useLayoutEffect, useMemo, useState } from 'react';
import { AiOutlineInfoCircle } from 'react-icons/ai';
import { BsCheck } from 'react-icons/bs';
import { FiInfo } from 'react-icons/fi';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import Apis from '../../../../../api';
import useIsTeamMember from '../../../../../hooks/useIsTeamMember';
import { HEADERMENU_PATH } from '../../../../../routes/RoutesURL';
import { RootState } from '../../../../../store/rootReducer';
import HSButton from '../../../../common/Button';
import LoginActions from '../../../../login/redux/actions';
import { InvestorType } from '../../../../login/redux/types';
import { InvestorStatus } from '../../../../login/utils/Status';
import MessageActions from '../../../../message/redux/actions';
import { investorTypeThresholds } from '../../Constant';
import { ONBOARDING_PATH } from '../../Onbording';
import ConfirmationModal from '../accreditedInvestor/modal/ConfirmationModal';
import OnboardingCard from '../common/Card';
import OnboardingRightContainer from '../common/Container';
import InfoPOP from '../common/InfoPop';

type Props = {
    setOpenPOP?: (f: boolean) => void;
    isSetting?: boolean;
    onClickCancel?: () => void;
};

const Accreditation: React.FC<Props> = ({ setOpenPOP, isSetting, onClickCancel }) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const location = useLocation();
    const isTeamMember: boolean = useIsTeamMember();

    const uuid = useSelector((state: RootState) => state.user.uuid);
    const investorType = useSelector((state: RootState) => state.user.investor?.investorType);
    const accreditation = useSelector((state: RootState) => state.user.investor?.accreditation);
    const profileStatus = useSelector((state: RootState) => state.user.investor?.status?.profile);
    const constants = useSelector((state: RootState) => state.constants.constants);

    const [type, setType] = useState<InvestorType | undefined>(undefined);
    const [value, setValue] = useState<string | undefined>(undefined);
    const [isValidate, setIsValidate] = useState(false);
    const [agree, setAgree] = useState(false);
    const [confirmationModal, setConfirmationModal] = useState(false);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    const isComingFromAccreditation = location.state?.isComingFromAccreditation;

    const accreditationList: {
        type: InvestorType;
        list: string[];
        tooltip: string;
    }[] = [
        {
            type: 'Individual Accredited',
            list: constants.individualAccreditation,
            tooltip: `I am signing up in my own personal capacity, and not on behalf of any company. At least one of the following statements apply to me`,
        },
        {
            type: 'Corporate Accredited',
            list: constants.individualAccreditation,
            tooltip: `I am signing up on behalf of a company/trust, and at least one of the following statements apply to the company/trust`,
        },
        {
            type: 'Institutional',
            list: constants.institutionalAccreditation,
            tooltip: `I represent an institutional investor and at least one of the following statements apply to my company`,
        },
    ];

    // const checkAccreditation = useCallback(() => {
    //     if (uuid) {
    //         setPageLoader(true);
    //         Apis.checkAccreditationAllow(uuid)
    //             .then(({ data }) => {
    //                 setIsAllow(data);
    //             })
    //             .catch((err) => {
    //                 dispatch(MessageActions.showMessage({ error: true, text: String(err) }));
    //             })
    //             .finally(() => {
    //                 setPageLoader(false);
    //             });
    //     }
    // }, [uuid, dispatch]);

    // useEffect(() => {
    //     checkAccreditation();
    // }, [checkAccreditation]);

    /**on type change clear data */
    const onChangeType = (t: InvestorType) => {
        setType(t);
        setValue(undefined);
    };

    useLayoutEffect(() => {
        if (investorType) {
            setType(investorType);
            setAgree(true);
        }
        if (accreditation && accreditation.status) {
            setValue(accreditation.status);
        }
        return () => {
            setType(undefined);
            setValue(undefined);
        };
    }, [accreditation, investorType]);

    const handleUpdateAccreditation = () => {
        if (!uuid) {
            return;
        }
        setLoading(true);
        Apis.updateAccreditation(uuid, {
            //accreditationType: value,
            investorType: type,
            status: profileStatus === InvestorStatus.PendingSelfAccreditation ? InvestorStatus.PendingNDA : undefined,
        })
            .then(({ data }) => {
                dispatch(LoginActions.updateInvestorInfo(data));
                setConfirmationModal(false);
                if (!setOpenPOP && !isSetting) {
                    navigate(`${HEADERMENU_PATH.onboarding}/${ONBOARDING_PATH.INVESTOR_PROFILE}`);
                } else {
                    if (onClickCancel) {
                        onClickCancel();
                    }
                    // dispatch(
                    //     MessageActions.showMessage({ error: false, text: 'Accreditation updated successfully!' }),
                    // );
                }
            })
            .catch((err) => {
                dispatch(MessageActions.showMessage({ error: true, text: String(err) }));
            })
            .finally(() => {
                setLoading(false);
                if (setOpenPOP) {
                    setOpenPOP(false);
                }
            });
    };
    /**On Click next */
    const onClickNext = () => {
        if (isTeamMember) {
            navigate(`${HEADERMENU_PATH.onboarding}/${ONBOARDING_PATH.INVESTOR_PROFILE}`);
            return;
        }
        setIsValidate(true);
        if (!type) {
            return;
        }
        // if (type === 'Individual Accredited' && !value) {
        //     return;
        // }
        // if (type === 'Institutional' && !value) {
        //     return;
        // }
        if (value === 'I am not accredited') {
            setOpen(true);
            return;
        }
        setIsValidate(false);
        if (!isComingFromAccreditation && investorType && investorType !== type) {
            setConfirmationModal(true);
            return;
        }
        handleUpdateAccreditation();
    };

    const accreditationTypes = useMemo(() => {
        if (type) {
            const data = investorTypeThresholds.find((i) => i.type === type);
            if (data) {
                return data;
            }
        }

        return null;
    }, [type]);

    const getSelectedCheckboxStyle = () => {
        return { backgroundColor: '#27AE60', borderColor: '#27AE60' };
    };

    return (
        <OnboardingRightContainer>
            <InfoPOP
                open={open}
                setOpen={setOpen}
                text='Thank you for your interest in Helicap Securities. Only accredited investors may view opportunities at this time. Please change your accreditation status or contact us at <a className="text-[#1B3E8C]" href="mailto:admin@heli-cap.com">admin@heli-cap.com</a> if you have any queries'
                icon={<FiInfo size={'1.8rem'} color="#26AE60" />}
            />
            <ConfirmationModal
                title="Confirmation"
                open={confirmationModal}
                onOk={handleUpdateAccreditation}
                onCancel={() => {
                    setConfirmationModal(false);
                    if (investorType) {
                        onChangeType(investorType);
                    }
                }}
                centered
                confirmationLoading={loading}
            />
            <div>
                <p className="page_title_onboarding">
                    Choose an investor account type: <span className="text-red-500">*</span>
                </p>
                <div className="flex justify-between items-center md:space-x-8 py-4 flex-wrap md:flex-nowrap font-[Lato]">
                    {accreditationList.map((a, index) => (
                        <OnboardingCard
                            key={`${index}_${a.type}`}
                            width="w-[48%] md:w-1/2 min-h-[74px]"
                            className="justify-center items-center text-center"
                            onClick={() => {
                                !isTeamMember && onChangeType(a.type);
                            }}
                            selected={type === a.type}
                            tooltip={a.tooltip ? <div className="text-center WorkSans">{a.tooltip}</div> : undefined}
                        >
                            {a.type}
                        </OnboardingCard>
                    ))}
                </div>
                {isValidate && !type && <span className="common_error relative -top-8">Please select type</span>}
            </div>

            <div className="pt-0">
                {accreditationTypes && (
                    <>
                        <div className="flex md:items-center justify-between flex-col md:flex-row">
                            <p className="font-[WorkSans-Semibold] text-[#20222B] font-semibold text-sm page_title_onboarding">
                                {accreditationTypes.title}
                            </p>
                        </div>
                        <div className="pt-2 pb-4 Lato">
                            <ol type="1" className="list-decimal text-[#20222B]">
                                {accreditationTypes.list.map((t) => (
                                    <li key={`${uuidv4()}`} className="list-item list-inside py-1">
                                        {t.text}{' '}
                                        {t.tooltip ? (
                                            <Tooltip
                                                title={
                                                    t.tooltip ? (
                                                        <div className="text-center WorkSans">{t.tooltip}</div>
                                                    ) : undefined
                                                }
                                            >
                                                <AiOutlineInfoCircle
                                                    color="#26AE60"
                                                    size={'1.3rem'}
                                                    className="inline"
                                                />
                                            </Tooltip>
                                        ) : null}
                                    </li>
                                ))}
                            </ol>
                        </div>
                        <p className="!font-[Lato] text-[#20222B] text-xs pb-4">
                            * Supporting documentation will be required to view our live deals.
                        </p>
                        <div>
                            <p className=" pr-4 md:pr-1 flex items-center text-sm text-color-404040">
                                <span
                                    onClick={() => {
                                        !isTeamMember && setAgree(!agree);
                                    }}
                                    style={agree ? getSelectedCheckboxStyle() : {}}
                                    className="h-5 w-5 border  border-[#27AE60] mx-1 flex justify-center items-center cursor-pointer bg-white"
                                >
                                    {agree && <BsCheck size={'1.5rem'} color="#fff" />}
                                </span>
                                Yes, I qualify
                            </p>
                        </div>
                        <div className="relative flex py-5 items-center">
                            <div className="flex-grow border-t border-[#C4CFE4]"></div>
                            <span className="flex-shrink mx-4 text-[#404040] font-[WorkSans]">OR</span>
                            <div className="flex-grow border-t border-[#C4CFE4]"></div>
                        </div>
                        <div>
                            <OnboardingCard onClick={() => setOpen(true)}>
                                I am not an accredited investor. None of the investment criteria apply to me.
                            </OnboardingCard>
                        </div>
                    </>
                )}
            </div>

            <div className="flex justify-center items-center mt-10 mb-5">
                <HSButton
                    title={isSetting ? 'Update' : 'Next'}
                    type="primary"
                    onClick={onClickNext}
                    loading={confirmationModal ? false : loading}
                    disabled={!agree}
                />
                {isSetting && onClickCancel && (
                    <HSButton title={'Cancel'} className="onboarding_btn ml-2" onClick={onClickCancel} light />
                )}
            </div>
        </OnboardingRightContainer>
    );
};

export default Accreditation;
